/* eslint-disable react/prop-types */
import { useState } from 'react'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Switch from '@mui/material/Switch'
import Chip from '@mui/material/Chip'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import LinearProgress from '@mui/material/LinearProgress'
import { ADMINISTRATOR_KEY, PERMISSION_GROUPS } from '../administrationConstants'
import { diffPermissions, pickEditableValues } from '../administrationUtils'

function PermissionRow({ label, hint, permissionKey, checked, changed, disabled, onToggle }) {
  return (
    <Stack
      direction="row"
      spacing={2}
      sx={{ alignItems: 'center', py: 1.25, borderTop: '1px solid', borderColor: 'divider' }}
    >
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="body1">{label}</Typography>
          {changed && <Chip label="alterado" size="small" color="warning" variant="outlined" />}
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {hint}
        </Typography>
        <Typography variant="caption" color="text.secondary" component="code">
          {permissionKey}
        </Typography>
      </Box>
      <Switch
        checked={checked}
        disabled={disabled}
        onChange={() => onToggle(permissionKey)}
        slotProps={{ input: { 'aria-label': label } }}
      />
    </Stack>
  )
}

function resolveAdministratorNotice(isTargetSuperAdministrator, canManageAdministrators) {
  if (isTargetSuperAdministrator) {
    return 'Administrador geral já é administrador. Esse perfil só é alterado direto no banco.'
  }
  if (!canManageAdministrators) return 'Somente o Administrador geral define quem é administrador.'
  return null
}

function changesLabel(count) {
  if (count === 0) return 'Nenhuma alteração'
  return count === 1 ? '1 alteração não salva' : `${count} alterações não salvas`
}

function UserPermissionsDialog({ user, canManageAdministrators, isSaving, onCancel, onSave }) {
  const [original] = useState(() => pickEditableValues(user))
  const [draft, setDraft] = useState(original)
  const changes = diffPermissions(original, draft)
  const changesCount = Object.keys(changes).length
  const isTargetSuperAdministrator = Boolean(user.isSuperAdministrator)
  const administratorNotice = resolveAdministratorNotice(
    isTargetSuperAdministrator,
    canManageAdministrators,
  )

  function handleToggle(key) {
    setDraft((current) => ({ ...current, [key]: !current[key] }))
  }

  return (
    <Dialog open onClose={isSaving ? undefined : onCancel} fullWidth maxWidth="sm">
      <DialogTitle>
        Permissões de {user.name || user.login}
        <Typography variant="body2" color="text.secondary" component="div">
          {user.login}
        </Typography>
      </DialogTitle>
      {isSaving && <LinearProgress />}
      <DialogContent>
        <Paper variant="outlined" sx={{ px: 2, py: 1.5, mb: 2 }}>
          <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
            <Box sx={{ flexGrow: 1 }}>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Administrador
                </Typography>
                {ADMINISTRATOR_KEY in changes && (
                  <Chip label="alterado" size="small" color="warning" variant="outlined" />
                )}
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Acessa o menu Administração e altera as permissões de outros usuários. Não define
                administradores.
              </Typography>
            </Box>
            <Switch
              checked={isTargetSuperAdministrator || draft[ADMINISTRATOR_KEY]}
              disabled={isTargetSuperAdministrator || !canManageAdministrators || isSaving}
              onChange={() => handleToggle(ADMINISTRATOR_KEY)}
              slotProps={{ input: { 'aria-label': 'Administrador' } }}
            />
          </Stack>
          {administratorNotice && (
            <Alert severity="info" sx={{ mt: 1 }}>
              {administratorNotice}
            </Alert>
          )}
        </Paper>

        {PERMISSION_GROUPS.map((group) => (
          <Box key={group.title} sx={{ mb: 2 }}>
            <Typography
              variant="overline"
              color="primary"
              component="h3"
              sx={{ fontWeight: 500, letterSpacing: 0.8, lineHeight: 2 }}
            >
              {group.title}
            </Typography>
            {group.permissions.map((permission) => (
              <PermissionRow
                key={permission.key}
                label={permission.label}
                hint={permission.hint}
                permissionKey={permission.key}
                checked={draft[permission.key]}
                changed={permission.key in changes}
                disabled={isSaving}
                onToggle={handleToggle}
              />
            ))}
          </Box>
        ))}
      </DialogContent>
      <DialogActions sx={{ px: 3, borderTop: '1px solid', borderColor: 'divider' }}>
        <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
          {changesLabel(changesCount)}
        </Typography>
        <Button onClick={onCancel} disabled={isSaving}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={() => onSave(changes)}
          disabled={changesCount === 0 || isSaving}
        >
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default UserPermissionsDialog
